import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, Button } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { CommonLoader } from '@/components/common'
import { useDonorContext } from '@/context'
import { useLoyaltyAuth } from '@/hooks'
import { ROUTES } from '@/constants'

export const DonorLoyaltyAuth = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { donor, loadingDonor } = useDonorContext()
  const { getLoyaltyAuth } = useLoyaltyAuth()
  const [error, setError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  const redirectUrl = searchParams.get('redirect_url')

  useEffect(() => {
    // Wait for donor context to finish loading
    if (loadingDonor) return

    let isCancelled = false

    const callLoyaltyAuth = async () => {
      if (!donor?.id) {
        navigate(ROUTES.LOGIN)
        return
      }

      try {
        const params = redirectUrl ? { redirect_url: redirectUrl } : {}
        const response = await getLoyaltyAuth(params)

        if (!isCancelled) {
          window.location.href = response?.data?.url
        }
      } catch (error) {
        if (isCancelled) return

        // If 401, redirect to login
        if (error?.response?.status === 401) {
          const currentUrl = window.location.href
          navigate(
            `${ROUTES.LOGIN}?redirect_url=${encodeURIComponent(currentUrl)}`
          )
        } else {
          console.error('Failed to get loyalty auth:', error)
          setError(
            error?.response?.data?.message ||
              'Failed to connect to the Loyalty Store. Please try again later.'
          )
        }
      }
    }

    callLoyaltyAuth()

    return () => {
      isCancelled = true
    }
  }, [donor?.id, loadingDonor, retryCount])

  const handleRetry = () => {
    setError(null)
    setRetryCount(c => c + 1)
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          px: 3,
          textAlign: 'center',
        }}
      >
        <ErrorOutlineIcon
          sx={{
            fontSize: 64,
            color: 'error.main',
            mb: 2,
          }}
        />
        <Typography
          variant='h5'
          fontWeight={600}
          color='text.primary'
          gutterBottom
        >
          Something went wrong
        </Typography>
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{ maxWidth: 400, mb: 4 }}
        >
          {error}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='outlined'
            size='large'
            onClick={() => navigate(ROUTES.HOME)}
          >
            Go Home
          </Button>
          <Button variant='contained' size='large' onClick={handleRetry}>
            Try Again
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CommonLoader color='primary' />
      <Typography variant='body1' color='text.secondary' sx={{ mt: 2 }}>
        Redirecting to Loyalty Store...
      </Typography>
    </Box>
  )
}
